import { useEffect, useCallback, type RefObject } from 'react';
import type { Sample } from '../../services/sample.service';
import { getKensukeJQuery } from '../../utils/jqueryDatatablesShim';

interface UseBulkSelectionArgs {
  selectedSampleIds: Set<number>;
  onSelectionChange: (selectedIds: Set<number> | ((prev: Set<number>) => Set<number>)) => void;
  samples: Sample[];
  tableRef: RefObject<HTMLTableElement | null>;
  dataTableRef: RefObject<unknown>;
}

/** attr 優先（jQuery .data キャッシュが data-id とズレることがある） */
function readCheckboxSampleId(el: HTMLElement): number | null {
  const $ = getKensukeJQuery();
  if (!$) return null;
  const raw = $(el).attr('data-id') ?? $(el).data('id');
  if (raw === undefined || raw === null || raw === '') return null;
  const id = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * DataTables の行チェックボックスと選択 Set を同期する（UI なし）。
 */
export function useBulkSelection({
  selectedSampleIds,
  onSelectionChange,
  samples,
  tableRef,
  dataTableRef,
}: UseBulkSelectionArgs): void {
  const handleCheckboxChange = useCallback((e: JQuery.Event) => {
    const $ = getKensukeJQuery();
    if (!$) return;

    const target = e.target as HTMLElement | null;
    if (!target) return;
    const id = readCheckboxSampleId(target);
    if (id == null) return;

    const isChecked = $(target).is(':checked');

    onSelectionChange((prev) => {
      const next = new Set(prev);
      if (isChecked) next.add(id);
      else next.delete(id);
      return next;
    });
    // onSelectionChange は useState setter 想定で安定
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAllChange = useCallback(
    (e: JQuery.Event) => {
      if (!tableRef.current) return;
      const $ = getKensukeJQuery();
      if (!$) return;

      const isChecked = $(e.target).is(':checked');
      $(tableRef.current).find('.sample-checkbox').prop('checked', isChecked);
      onSelectionChange(isChecked ? new Set(samples.map((s) => s.id)) : new Set());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [samples]
  );

  const syncSelectAllCheckboxes = useCallback(
    (allChecked: boolean, indeterminate: boolean) => {
      if (!tableRef.current) return;
      const $ = getKensukeJQuery();
      if (!$) return;

      const selectAllCheckbox = $(tableRef.current).find('#selectAllCheckbox');
      if (selectAllCheckbox.length > 0) {
        selectAllCheckbox.prop('checked', allChecked);
        (selectAllCheckbox[0] as HTMLInputElement).indeterminate = indeterminate;
      }

      const mobileSelectAll = document.getElementById('mobileSelectAllCheckbox') as HTMLInputElement | null;
      if (mobileSelectAll) {
        mobileSelectAll.checked = allChecked;
        mobileSelectAll.indeterminate = indeterminate;
      }
    },
    [tableRef]
  );

  useEffect(() => {
    if (!tableRef.current || !dataTableRef.current) return;

    const $ = getKensukeJQuery();
    if (!$ || !$.fn.DataTable || !$.fn.DataTable.isDataTable(tableRef.current)) {
      return;
    }

    $(tableRef.current)
      .off('change', '.sample-checkbox')
      .on('change', '.sample-checkbox', handleCheckboxChange);
    $(tableRef.current)
      .off('change', '#selectAllCheckbox')
      .on('change', '#selectAllCheckbox', handleSelectAllChange);

    return () => {
      if (!tableRef.current) return;
      const $cleanup = getKensukeJQuery();
      if (!$cleanup) return;
      $cleanup(tableRef.current).off('change', '.sample-checkbox');
      $cleanup(tableRef.current).off('change', '#selectAllCheckbox');
    };
  }, [tableRef, dataTableRef, handleCheckboxChange, handleSelectAllChange]);

  useEffect(() => {
    const mobileSelectAll = document.getElementById('mobileSelectAllCheckbox');
    if (!mobileSelectAll) return;

    const onMobileSelectAll = (ev: Event) => {
      handleSelectAllChange(ev as unknown as JQuery.Event);
    };
    mobileSelectAll.addEventListener('change', onMobileSelectAll);
    return () => mobileSelectAll.removeEventListener('change', onMobileSelectAll);
  }, [handleSelectAllChange, samples.length]);

  const updateCheckboxStates = useCallback(() => {
    if (!tableRef.current || !dataTableRef.current) return;

    const $ = getKensukeJQuery();
    if (!$ || !$.fn.DataTable || !$.fn.DataTable.isDataTable(tableRef.current)) {
      return;
    }

    $(tableRef.current)
      .find('.sample-checkbox')
      .each(function (this: HTMLElement) {
        const id = readCheckboxSampleId(this);
        if (id == null) return;
        const checked = selectedSampleIds.has(id);
        $(this).prop('checked', checked);
        $(this).closest('tr').toggleClass('sample-row--selected', checked);
      });

    const allChecked = samples.length > 0 && samples.every((s) => selectedSampleIds.has(s.id));
    const someChecked = samples.some((s) => selectedSampleIds.has(s.id));
    const indeterminate = someChecked && !allChecked;
    syncSelectAllCheckboxes(allChecked, indeterminate);
  }, [selectedSampleIds, samples, tableRef, dataTableRef, syncSelectAllCheckboxes]);

  useEffect(() => {
    updateCheckboxStates();
  }, [updateCheckboxStates]);

  useEffect(() => {
    if (!tableRef.current || !dataTableRef.current) return;

    const $ = getKensukeJQuery();
    if (!$ || !$.fn.DataTable || !$.fn.DataTable.isDataTable(tableRef.current)) {
      return;
    }

    $(tableRef.current).off('draw.dt.kensukeBulk').on('draw.dt.kensukeBulk', updateCheckboxStates);

    return () => {
      if (!tableRef.current) return;
      const $cleanup = getKensukeJQuery();
      if ($cleanup) $cleanup(tableRef.current).off('draw.dt.kensukeBulk');
    };
  }, [tableRef, dataTableRef, updateCheckboxStates]);
}
